import { isElementScrollbarVisible, useScrollListener } from '@helpers';
import { useEntitiesTableData } from '@terminal/react-query-hooks/entities/useEntities';
import { type RefObject, useLayoutEffect } from 'react';

export const SCROLL_PAGINATED_ENTITIES_PAGE_SIZE = 20;

type UseScrollPaginatedEntitiesOptions = {
  scrollContainerRef?: RefObject<HTMLDivElement | null>;
  scrollFillKey?: number;
};

export const useScrollPaginatedEntities = (
  tags: string[],
  enabled: boolean,
  {
    scrollContainerRef,
    scrollFillKey = 0,
  }: UseScrollPaginatedEntitiesOptions = {}
) => {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useEntitiesTableData(
      enabled ? tags : [],
      SCROLL_PAGINATED_ENTITIES_PAGE_SIZE
    );

  const pages = data?.pages ?? [];
  const entities = pages.flat().filter(Boolean);

  useScrollListener(
    scrollContainerRef ?? { current: null },
    () => {
      if (hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }

      return hasNextPage;
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
    { isGlobalScrollListened: false, offset: 20 }
  );

  useLayoutEffect(() => {
    if (!enabled || !scrollContainerRef?.current || !hasNextPage) {
      return;
    }

    if (isFetchingNextPage) {
      return;
    }

    const hasScroll = isElementScrollbarVisible(scrollContainerRef, 'vertical');

    if (hasScroll) {
      return;
    }

    void fetchNextPage();
  }, [
    enabled,
    scrollContainerRef,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    scrollFillKey,
  ]);

  return {
    entities,
    loadedPageCount: pages.length,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  };
};
